import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  Close as CloseIcon,
  DeleteOutlineRounded as DeleteOutlineIcon,
} from '@mui/icons-material';
import { api, DroneViewCandidate, DroneViewEntry, DroneViewProbe } from '../../services/api';
import SitePlanDroneView from '../../components/siteplan/SitePlanDroneView';

/**
 * Manage Drone View (admin): attach an aerial / 360° tour URL to a project.
 * It shows up as the "Drone" mode on that project's site-plan card
 * (2D | 3D | 3DS | Drone | Views).
 *
 * The tour is iframed, so every URL is probed first: a developer's marketing
 * page usually refuses framing (X-Frame-Options / frame-ancestors) but wraps an
 * embeddable tour, and the probe offers that inner URL instead.
 */
interface ManageDroneViewDialogProps {
  open: boolean;
  onClose: () => void;
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value.trim()).protocol === 'https:';
  } catch {
    return false;
  }
}

const listBorder = '1px solid rgb(var(--color-text-rgb) / 0.12)';

export default function ManageDroneViewDialog({ open, onClose }: ManageDroneViewDialogProps) {
  const [entries, setEntries] = useState<DroneViewEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidates, setCandidates] = useState<DroneViewCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [project, setProject] = useState<DroneViewCandidate | null>(null);

  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [probe, setProbe] = useState<DroneViewProbe | null>(null);
  const [probing, setProbing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const existing = useMemo(
    () => (project ? entries.find((entry) => entry.projectId === project.projectId) ?? null : null),
    [entries, project],
  );

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await api.getDroneViews());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load drone views');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetEditor = useCallback(() => {
    setProject(null);
    setCandidateSearch('');
    setUrl('');
    setLabel('');
    setIsActive(true);
    setProbe(null);
    setShowPreview(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    setError('');
    setNotice('');
    resetEditor();
    void loadEntries();
  }, [open, loadEntries, resetEditor]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCandidatesLoading(true);
    const timer = window.setTimeout(() => {
      api.getDroneViewCandidates(candidateSearch)
        .then((rows) => { if (!cancelled) setCandidates(rows); })
        .catch(() => { if (!cancelled) setCandidates([]); })
        .finally(() => { if (!cancelled) setCandidatesLoading(false); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [open, candidateSearch]);

  /** Load a project into the editor, prefilled from its saved drone view if it has one. */
  const selectProject = useCallback((next: DroneViewCandidate | null) => {
    setProject(next);
    // Keep the field text in step; MUI reports a programmatic change as 'reset', which the input handler ignores.
    setCandidateSearch(next?.projectName ?? '');
    setProbe(null);
    setShowPreview(false);
    const saved = next ? entries.find((entry) => entry.projectId === next.projectId) : undefined;
    setUrl(saved?.url ?? '');
    setLabel(saved?.label ?? '');
    setIsActive(saved?.isActive ?? true);
  }, [entries]);

  const runProbe = useCallback(async (target: string) => {
    if (!isHttpsUrl(target)) {
      setProbe(null);
      return;
    }
    setProbing(true);
    try {
      setProbe(await api.probeDroneViewUrl(target.trim()));
    } catch (err) {
      setProbe({
        url: target,
        embeddable: null,
        reason: err instanceof Error ? err.message : 'Unable to check the URL',
        suggestedUrl: null,
        title: null,
      });
    } finally {
      setProbing(false);
    }
  }, []);

  const handleUseSuggested = useCallback(() => {
    if (!probe?.suggestedUrl) return;
    const suggested = probe.suggestedUrl;
    setUrl(suggested);
    void runProbe(suggested);
  }, [probe, runProbe]);

  const handleSave = useCallback(async () => {
    if (!project || !isHttpsUrl(url)) return;
    if (probe?.url === url.trim() && probe.embeddable === false
      && !window.confirm('This page blocks embedding, so agents will see an empty frame. Save anyway?')) {
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.saveDroneView(project.projectId, { url: url.trim(), label: label.trim() || null, isActive });
      await loadEntries();
      setNotice(`Drone view saved for ${project.projectName}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save the drone view');
    } finally {
      setSaving(false);
    }
  }, [project, url, label, isActive, probe, loadEntries]);

  const handleRemove = useCallback(async () => {
    if (!project || !existing) return;
    if (!window.confirm(`Remove the drone view from ${project.projectName}?`)) return;
    setRemoving(true);
    setError('');
    setNotice('');
    try {
      await api.removeDroneView(project.projectId);
      await loadEntries();
      setNotice(`Drone view removed from ${project.projectName}.`);
      resetEditor();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to remove the drone view');
    } finally {
      setRemoving(false);
    }
  }, [project, existing, loadEntries, resetEditor]);

  const urlValid = isHttpsUrl(url);
  const probeIsCurrent = probe?.url === url.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '95vh' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Manage Drone View</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
          {/* Projects that already have a drone view */}
          <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, maxHeight: '70vh', overflowY: 'auto', border: listBorder, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: listBorder }}>
              <Typography variant="subtitle2">Drone views</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={resetEditor}>New</Button>
            </Box>
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
            ) : entries.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2, color: 'var(--color-text-muted)' }}>
                No project has a drone view yet.
              </Typography>
            ) : entries.map((entry) => {
              const selected = project?.projectId === entry.projectId;
              return (
                <Box
                  key={entry.projectId}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectProject({ projectId: entry.projectId, projectName: entry.projectName, district: null })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') selectProject({ projectId: entry.projectId, projectName: entry.projectName, district: null });
                  }}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    borderBottom: listBorder,
                    bgcolor: selected ? 'rgb(var(--color-accent-rgb) / 0.14)' : 'transparent',
                    '&:hover': { bgcolor: 'rgb(var(--color-accent-rgb) / 0.08)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
                      {entry.projectName}
                    </Typography>
                    {!entry.isActive && <Chip size="small" label="Hidden" />}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-muted)' }} noWrap>
                    {entry.url}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Editor */}
          <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
            <Autocomplete
              options={candidates}
              loading={candidatesLoading}
              value={project}
              onChange={(_, value) => selectProject(value)}
              inputValue={candidateSearch}
              onInputChange={(_, value, reason) => { if (reason !== 'reset') setCandidateSearch(value); }}
              getOptionLabel={(option) => option.projectName}
              isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
              renderOption={(props, option) => (
                <li {...props} key={option.projectId}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.projectName}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                      {[option.projectId, option.district ? `D${String(option.district).replace(/^D/i, '')}` : null]
                        .filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Project" placeholder="Search SG projects by name or id" />
              )}
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                fullWidth
                label="Drone view URL"
                placeholder="https://…"
                value={url}
                disabled={!project}
                onChange={(event) => {
                  setUrl(event.target.value);
                  setShowPreview(false);
                }}
                onBlur={() => { if (urlValid && !probeIsCurrent) void runProbe(url); }}
                error={Boolean(url.trim()) && !urlValid}
                helperText={url.trim() && !urlValid ? 'Must be an https link' : 'Paste the 360° / aerial tour link'}
              />
              <Button
                variant="outlined"
                disabled={!urlValid || probing}
                onClick={() => void runProbe(url)}
                sx={{ height: 40, flexShrink: 0 }}
              >
                {probing ? <CircularProgress size={16} /> : 'Check'}
              </Button>
            </Box>

            {probe && probeIsCurrent && (
              probe.embeddable === true ? (
                <Alert severity="success">This page can be embedded.</Alert>
              ) : probe.embeddable === false ? (
                <Alert
                  severity="warning"
                  action={probe.suggestedUrl ? (
                    <Button color="inherit" size="small" onClick={handleUseSuggested}>Use this URL</Button>
                  ) : undefined}
                >
                  This page blocks embedding ({probe.reason}).
                  {probe.suggestedUrl ? (
                    <>
                      {' '}It contains an embeddable tour:
                      <Box component="code" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all', fontSize: '0.75rem' }}>
                        {probe.suggestedUrl}
                      </Box>
                    </>
                  ) : ' Ask the developer for the direct tour link.'}
                </Alert>
              ) : (
                <Alert severity="info">{probe.reason} You can still preview it below.</Alert>
              )
            )}

            <TextField
              size="small"
              fullWidth
              label="Caption (optional)"
              placeholder={probe?.title || 'Shown under the tour'}
              value={label}
              disabled={!project}
              inputProps={{ maxLength: 100 }}
              onChange={(event) => setLabel(event.target.value)}
            />

            <FormControlLabel
              control={<Switch checked={isActive} disabled={!project} onChange={(event) => setIsActive(event.target.checked)} />}
              label={isActive ? 'Visible to agents' : 'Hidden from agents'}
            />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disabled={!project || !urlValid || saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                onClick={() => void handleSave()}
              >
                {existing ? 'Save changes' : 'Add drone view'}
              </Button>
              <Button variant="outlined" disabled={!urlValid} onClick={() => setShowPreview((value) => !value)}>
                {showPreview ? 'Hide preview' : 'Preview'}
              </Button>
              {existing && (
                <Button
                  color="error"
                  disabled={removing}
                  startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                  onClick={() => void handleRemove()}
                  sx={{ ml: 'auto' }}
                >
                  Remove
                </Button>
              )}
            </Box>

            {showPreview && urlValid && (
              <SitePlanDroneView droneView={{ url: url.trim(), label: label.trim() || null }} />
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
